import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";
import { getProjectWiseCost, getMonitoringProjectList } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { getAllUnits } from "../../redux/unitSlice/unitSlice";
import CustomTable from '../../components/Common/CustomTable/CustomTable';
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import PMGProjectIcon from "../../assets/icons/pmg-project.svg";
import { Loader2 } from "lucide-react";

const PROJECT_TYPE_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "EPC", value: "EPC" },
    { label: "BTG", value: "BTG" },
    { label: "OTHER", value: "OTHER" },
];

const COST_LEVEL_OPTIONS = [
    { label: "Direct Material", value: "DIRECT_MATERIAL" },
    { label: "Conversion Cost", value: "CONVERSION_COST" },
    { label: "Factory Cost Level", value: "TOTAL_COST" },
];

const VALUE_MODE_OPTIONS = [
    { label: "Crore", value: "CRORE" },
    { label: "Percent", value: "PERCENT" },
];

// Unlike DMProjectWiseReport, this report has no "Overall vs Year Wise"
// mode switch. Every financial year (from the current FY onward) is ALWAYS
// shown as its own column (image 1). This toggle only controls whether
// each year column is a single "Total" figure (YEARLY, image 1) or expands
// into Q1 | Q2 | Q3 | Q4 | Total sub-columns (QUARTERLY, image 2).
const VIEW_MODE_OPTIONS = [
    { label: "Yearly View", value: "YEARLY" },
    { label: "Quarterly View", value: "QUARTERLY" },
];

// Values from the API can come formatted with thousands separators
// (e.g. "10,518.00"). parseFloat stops at the first comma, so strip
// commas before parsing.
const toNumber = (val) => {
    const cleaned = String(val ?? "").replace(/,/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// Builds a normalized "IO_NO-REV_NO" key so matching works regardless of
// whether the two APIs send numbers, strings, extra whitespace, or missing
// REV_NO.
const makeProjectKey = (ioNo, revNo) => {
    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();
    return `${io}-${rev}`;
};

// Appends a "%" suffix to a displayed cell value when Value Mode is Percent.
const formatCellValue = (val, valueMode) => {
    if (val === undefined || val === null || val === "") return val;
    return valueMode === "PERCENT" ? `${val}%` : val;
};

// The financial year (Apr -> Mar) that "today" falls in, expressed as its
// starting calendar year, e.g. April 2026 - March 2027 -> 2026.
// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
    const today = new Date();
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
    if (!startDate) return null;

    const [day, month, year] = startDate.split("-").map(Number);

    if (!day || !month || !year) return null;

    return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
// NEW: getUptoMarchValue below now takes fyStart as an explicit param, and
// UPTO_MARCH_LABEL is computed inside the component from the memoized value.
// const CURRENT_FY_START = getCurrentFYStart();
// const CURRENT_FY_START = "2027";
// console.log(CURRENT_FY_START)

const getFYStartYear = (fyKey) => Number(String(fyKey).split("-")[0]);

// "2026-2027" -> "2026-27", used for column headers (image 1 style).
const formatFYShort = (fyKey) => {
    const [start, end] = String(fyKey).split("-");
    return `${start}-${String(end).slice(-2)}`;
};

// "Q1-2026" -> "2026-2027"
const getFYFromQuarterKey = (key) => {
    const match = /^Q[1-4]-(\d{4})$/.exec(key);
    if (!match) return null;
    const fyStart = Number(match[1]);
    return `${fyStart}-${fyStart + 1}`;
};

const getQuarterLabel = (key) => key.split("-")[0];

// "Upto March <FY start>" figure for a project/group/grand-total record: the
// API's baseline "upto" figure plus every financial year that falls strictly
// before the current FY. This report has no Year dropdown to anchor to, so
// unlike DMProjectWiseReport there's no per-selection variant of this.
// fyStart is now passed in explicitly by the component, derived from systemDate.
const getUptoMarchValue = (record, fyStart) => {
    if (!record) return "0.00";
    let sum = toNumber(record.financial_years?.upto);
    Object.entries(record.financial_years || {}).forEach(([fyKey, val]) => {
        if (fyKey === "upto") return;
        if (getFYStartYear(fyKey) < fyStart) {
            sum += toNumber(val);
        }
    });
    return sum.toFixed(2);
};

// OLD: const UPTO_MARCH_LABEL = `Upto Mar '${String(CURRENT_FY_START).slice(-2)}`; (fixed once at import time)
// NEW: computed inside the component from the memoized CURRENT_FY_START (see below).

const DMReport = () => {
    const dispatch = useDispatch();

    // Backend-dependent filters
    const [projectType, setProjectType] = useState("ALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("PERCENT");

    // Frontend-only: Yearly (image 1) vs Quarterly (image 2) column layout.
    const [viewMode, setViewMode] = useState("YEARLY");

    // Frontend-only: whether the "Upto March" column is shown. Only relevant
    // (and only offered in the UI) when Value Mode is Crore - Upto March has
    // no meaning as a percentage figure and is never shown in Percent mode.
    const [showUptoMarch, setShowUptoMarch] = useState(false);

    // Master, unfiltered list of projects — used only to populate the
    // "Projects Filter" dropdown. Unlike allProjectsFlat, this does NOT
    // shrink when the API response is filtered down to selected projects.
    const [masterProjects, setMasterProjects] = useState([]);

    // Holds "IO_NO-REV_NO" keys selected in the multi-select. The actual
    // {IO_NO, REV_NO} objects sent to the API are derived via
    // projectKeyToProject below.
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    const [unit, setUnit] = useState("ALL");

    const { projectWiseCost, gettingProjectWiseCost, monitoringProjectList } = useSelector(
        (state) => state.unitReport
    );
    const { allUnitsList } = useSelector((state) => state.unit);
    const { systemDate } = useSelector((state) => state.otherProjects);

    // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
    // NEW: derive it from the admin-configured System Start Date, falling back
    // to today's date until systemDate has loaded.
    const CURRENT_FY_START = useMemo(
        () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
        [systemDate]
    );

    const UPTO_MARCH_LABEL = `Upto Mar '${String(CURRENT_FY_START).slice(-2)}`;

    const reportData = projectWiseCost?.data ?? projectWiseCost ?? null;

    const groups = reportData?.groups ?? [];
    const yearLabels = reportData?.labels ?? [];
    const quarterLabels = reportData?.quarterly_labels ?? [];

    const fetchReport = () => {
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProject.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({ IO_NO, REV_NO }));

        dispatch(
            getProjectWiseCost({
                PROJECT_TYPE: projectType,
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                MNF_UNIT: unit === "ALL" ? undefined : unit,
                projects: projectsPayload,
            })
        );
    };

    const fetchMonitoringProjects = () => {
        dispatch(
            getMonitoringProjectList({
                PROJECT_TYPE: projectType,
            })
        );
    };

    const unitOptions = useMemo(() => {
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        const options = list.map((u) => ({
            label: u.UNIT_DISPLAY_NAME,
            value: u.MNF_UNIT,
        }));
        return [{ label: "All", value: "ALL" }, ...options];
    }, [allUnitsList]);

    // Initial load with default filters.
    useEffect(() => {
        setSelectedProjectKeys([]); // reset so masterProjects can repopulate for the new type
        fetchReport();
        fetchMonitoringProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectType]);

    useEffect(() => {
        dispatch(getAllUnits());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch whenever a backend-dependent filter changes.
    // NOTE: viewMode is intentionally NOT here — the API already returns
    // every year/quarter in one payload, so switching layout is purely a
    // frontend re-slice of existing data.
    // Skip the very first run — the [projectType] effect above already
    // fires the initial fetch, so this effect would otherwise double it.
    const isProjectWiseCostMount = useRef(true);

    useEffect(() => {
        if (isProjectWiseCostMount.current) {
            isProjectWiseCostMount.current = false;
            return;
        }

        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costLevel, valueMode, selectedProjectKeys.join(","), unit]);

    // Every project across every group, used to populate the "Selected
    // Projects" dropdown. Only capture the full project list when the
    // response was NOT filtered by a project selection (i.e.
    // selectedProjectKeys is empty), so the dropdown's option list stays
    // stable even after the table is filtered down to a subset.
    useEffect(() => {
        if (selectedProjectKeys.length === 0 && groups.length) {
            setMasterProjects((prev) => {
                const map = new Map(
                    prev.map((project) => [makeProjectKey(project.IO_NO, project.REV_NO), project])
                );

                groups.forEach((group) => {
                    group.projects?.forEach((project) => {
                        map.set(makeProjectKey(project.IO_NO, project.REV_NO), {
                            IO_NO: project.IO_NO,
                            REV_NO: project.REV_NO,
                            PROJ_NAME: project.PROJ_NAME,
                            PROJECT_TYPE: group.PROJECT_TYPE,
                        });
                    });
                });

                return Array.from(map.values());
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    // What's actually shown in the "Projects Filter" dropdown — narrowed to
    // the currently selected Project Type, without discarding the other
    // types from masterProjects (they stay cached for when the user
    // switches back).
    const visibleProjectsForFilter = useMemo(() => {
        if (projectType === "ALL") return masterProjects;
        return masterProjects.filter((project) => project.PROJECT_TYPE === projectType);
    }, [masterProjects, projectType]);

    // Maps a "IO_NO-REV_NO" key (what the multi-select stores) back to the
    // full { IO_NO, REV_NO, PROJ_NAME } object (what the API needs).
    const projectKeyToProject = useMemo(() => {
        const map = new Map();
        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });
        return map;
    }, [masterProjects]);

    // FY labels from the current financial year onward — these become the
    // year columns (or year-group columns in Quarterly view).
    const visibleYearLabels = useMemo(() => {
        return yearLabels
            .filter((fy) => getFYStartYear(fy) >= CURRENT_FY_START)
            .sort((a, b) => getFYStartYear(a) - getFYStartYear(b));
    }, [yearLabels, CURRENT_FY_START]);

    // Quarter keys grouped under their parent FY, e.g.
    // { "2026-2027": ["Q1-2026", "Q2-2026", "Q3-2026", "Q4-2026"] }.
    const groupedQuarterKeys = useMemo(() => {
        const map = {};

        quarterLabels.forEach((key) => {
            const fy = getFYFromQuarterKey(key);
            if (!fy || getFYStartYear(fy) < CURRENT_FY_START) return;

            if (!map[fy]) map[fy] = [];
            map[fy].push(key);
        });

        Object.keys(map).forEach((fy) => {
            map[fy].sort((a, b) => a.localeCompare(b));
        });

        return map;
    }, [quarterLabels, CURRENT_FY_START]);

    // Normalized set of "IO_NO-REV_NO" keys returned by the monitoring API.
    // Handles a few common response shapes: a bare array, { data: [...] },
    // or { data: { projects: [...] } }.
    const monitoringProjectMap = useMemo(() => {
        let list = monitoringProjectList;

        if (list && !Array.isArray(list)) {
            list = list.data ?? list.projects ?? list.data?.projects ?? [];
        }
        if (!Array.isArray(list)) list = [];

        return new Set(list.map((item) => makeProjectKey(item.IO_NO, item.REV_NO)));
    }, [monitoringProjectList]);

    const cellRender = (val) => formatCellValue(val, valueMode);

    const columnTotals = useMemo(() => {
        const totals = { uptoMarch: 0, total: 0 };
        visibleYearLabels.forEach((year) => {
            totals[`year_${year}`] = 0;
            (groupedQuarterKeys[year] || []).forEach((qKey) => {
                totals[`q_${qKey}`] = 0;
            });
        });

        groups.forEach((group) => {
            if (projectType !== "ALL" && group.PROJECT_TYPE !== projectType) return;

            const filteredProjects = (group.projects?.filter((project) => {
                if (projectType === "ALL") return true;
                return monitoringProjectMap.has(makeProjectKey(project.IO_NO, project.REV_NO));
            }) || []).filter((project) => {
                if (unit === "ALL") return true;
                return (project.units || []).some((u) => u.MNF_UNIT === unit);
            });

            filteredProjects.forEach((project) => {
                totals.uptoMarch += toNumber(getUptoMarchValue(project, CURRENT_FY_START));
                visibleYearLabels.forEach((year) => {
                    const yearVal = project.financial_years?.[year] ?? "0.00";
                    totals[`year_${year}`] += toNumber(yearVal);

                    (groupedQuarterKeys[year] || []).forEach((qKey) => {
                        const qVal = project.quarters?.[qKey] ?? "0.00";
                        totals[`q_${qKey}`] += toNumber(qVal);
                    });
                });
                totals.total += toNumber(project.total);
            });
        });

        return totals;
    }, [groups, visibleYearLabels, groupedQuarterKeys, projectType, monitoringProjectMap, unit, CURRENT_FY_START]);

    const columns = useMemo(() => {
        const ioNoColumn = {
            title: "IO No.",
            dataIndex: "IO_NO",
            key: "IO_NO",
            fixed: "left",
            width: 90,
            render: (text, record) => (record.isTotalRow ? "" : text),
        };

        // Project name row, with the project's type shown italicized
        // underneath (matches "P1 (PROJECT TYPE in ITALIC)" in image 2).
        const projectColumn = {
            title: "Project",
            dataIndex: "projectName",
            key: "projectName",
            fixed: "left",
            width: 280,
            render: (text, record) => {
                if (record.isTotalRow) {
                    return <span style={{ fontWeight: 800, fontSize: 16 }}>{text}</span>;
                }
                return (
                    <div>
                        <div>{text}</div>
                        {record.PROJECT_TYPE && (
                            <div style={{ fontStyle: "italic", fontSize: 12, color: "#667085" }}>
                                {record.PROJECT_TYPE}
                            </div>
                        )}
                    </div>
                );
            },
        };

        const uptoMarchColumn = valueMode === "PERCENT" || !showUptoMarch
            ? []
            : [{
                title: UPTO_MARCH_LABEL,
                dataIndex: "uptoMarch",
                key: "uptoMarch",
                width: 130,
                align: "right",
                render: cellRender,
            }];

        // One column per FY (Yearly view), or one column-group per FY with
        // Q1-Q4 + Total children (Quarterly view) — this is the piece that
        // differs from DMProjectWiseReport, which showed unit columns
        // instead of year/quarter columns.
        const yearColumns = visibleYearLabels.map((year) => {
            const yearKey = `year_${year}`;

            if ((columnTotals[yearKey] ?? 0) === 0) {
                return null;
            }

            if (viewMode === "QUARTERLY") {
                const quarterKeys = (groupedQuarterKeys[year] || []).filter((qKey) => {
                    return (columnTotals[`q_${qKey}`] ?? 0) !== 0;
                });
                return {
                    title: year,
                    key: `group_${year}`,
                    children: [
                        ...quarterKeys.map((qKey) => ({
                            title: getQuarterLabel(qKey),
                            dataIndex: `q_${qKey}`,
                            key: `q_${qKey}`,
                            width: 100,
                            align: "right",
                            render: cellRender,
                        })),
                        {
                            title: "Total",
                            dataIndex: yearKey,
                            key: yearKey,
                            width: 110,
                            align: "right",
                            render: cellRender,
                        },
                    ],
                };
            }

            return {
                title: formatFYShort(year),
                dataIndex: yearKey,
                key: yearKey,
                width: 110,
                align: "right",
                render: cellRender,
            };
        }).filter(Boolean);

        const overallColumn = {
            title: valueMode === "PERCENT" ? "Overall %" : "Overall",
            dataIndex: "total",
            key: "total",
            width: 130,
            align: "right",
            fixed: "right",
            render: (value, record) => {
                if (valueMode === "PERCENT" || showUptoMarch) {
                    return cellRender(value);
                }

                const uptoVal = toNumber(record?.uptoMarch);

                return cellRender((toNumber(value) - uptoVal).toFixed(2));
            },
        };

        // return [ioNoColumn, projectColumn, uptoMarchColumn, ...yearColumns, overallColumn];
        return [projectColumn, ...uptoMarchColumn, ...yearColumns, overallColumn];
    }, [visibleYearLabels, groupedQuarterKeys, viewMode, valueMode, CURRENT_FY_START, columnTotals, showUptoMarch]);

    const tableData = useMemo(() => {
        const rows = [];

        const grandRow = { uptoMarch: 0, total: 0 };
        visibleYearLabels.forEach((year) => {
            grandRow[`year_${year}`] = 0;
            (groupedQuarterKeys[year] || []).forEach((qKey) => {
                grandRow[`q_${qKey}`] = 0;
            });
        });

        groups.forEach((group) => {
            // When a specific project type is selected, only include that
            // group's projects — same behavior as DMProjectWiseReport,
            // just flattened into one table instead of collapsible
            // sections.
            if (projectType !== "ALL" && group.PROJECT_TYPE !== projectType) return;

            // const filteredProjects = group.projects?.filter((project) => {
            //     if (projectType === "ALL") return true;
            //     return monitoringProjectMap.has(makeProjectKey(project.IO_NO, project.REV_NO));
            // }) || [];

            const filteredProjects = (group.projects?.filter((project) => {
                if (projectType === "ALL") return true;
                return monitoringProjectMap.has(makeProjectKey(project.IO_NO, project.REV_NO));
            }) || []).filter((project) => {
                // Hide projects with no data under the currently selected unit.
                // An empty `units` array means this project has zero contribution
                // from `unit`, even though financial_years/total are still present
                // (just zeroed out) on the project object itself.
                if (unit === "ALL") return true;
                return (project.units || []).some((u) => u.MNF_UNIT === unit);
            });

            filteredProjects.forEach((project) => {
                const row = {
                    key: makeProjectKey(project.IO_NO, project.REV_NO),
                    IO_NO: project.IO_NO,
                    projectName: project.PROJ_NAME,
                    PROJECT_TYPE: group.PROJECT_TYPE,
                };

                row.uptoMarch = getUptoMarchValue(project, CURRENT_FY_START);
                grandRow.uptoMarch += toNumber(row.uptoMarch);

                visibleYearLabels.forEach((year) => {
                    const yearVal = project.financial_years?.[year] ?? "0.00";
                    row[`year_${year}`] = yearVal;
                    grandRow[`year_${year}`] += toNumber(yearVal);

                    (groupedQuarterKeys[year] || []).forEach((qKey) => {
                        const qVal = project.quarters?.[qKey] ?? "0.00";
                        row[`q_${qKey}`] = qVal;
                        grandRow[`q_${qKey}`] += toNumber(qVal);
                    });
                });

                row.total = project.total ?? "0.00";
                grandRow.total += toNumber(row.total);

                rows.push(row);
            });
        });

        // A summed grand total of percentages isn't a meaningful figure
        // (e.g. four 25% quarters don't add up to a real 100%), so the
        // bottom Overall row is hidden in Percent mode.
        if (valueMode === "PERCENT") {
            return rows;
        }

        const overallRow = {
            key: "overall-total",
            projectName: "OVERALL",
            isTotalRow: true,
            uptoMarch: grandRow.uptoMarch.toFixed(2),
            total: grandRow.total.toFixed(2),
        };
        visibleYearLabels.forEach((year) => {
            overallRow[`year_${year}`] = grandRow[`year_${year}`].toFixed(2);
            (groupedQuarterKeys[year] || []).forEach((qKey) => {
                overallRow[`q_${qKey}`] = grandRow[`q_${qKey}`].toFixed(2);
            });
        });

        return [...rows, overallRow];
    }, [groups, visibleYearLabels, groupedQuarterKeys, projectType, monitoringProjectMap, valueMode, unit, CURRENT_FY_START]);

    return (
        <div className="pb-14">
            <div className="px-5 flex flex-col gap-6 pt-8">
                {/* HEADER */}
                <div className="flex gap-3">
                    <img src={PMGProjectIcon} alt="icon" />

                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            DM Analytics: UNIT WISE - PROJECT WISE
                        </div>

                        <div className="text-gray-500 text-sm">
                            * All figures are in Rs Crores or in Percent depending on selected filter.
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-6 mt-2">
                    <div className="flex flex-wrap items-start justify-start gap-4 p-4 bg-[#F9FAFB] border border-[#EAECF0] rounded-xl">
                        {/* Type of Project */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Type of Project
                            </span>
                            <CustomSelectWithFilter
                                data={PROJECT_TYPE_OPTIONS}
                                value={projectType}
                                onChange={setProjectType}
                                placeholder="Type of Project"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Cost Level */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Cost Level
                            </span>
                            <CustomSelectWithFilter
                                data={COST_LEVEL_OPTIONS}
                                value={costLevel}
                                onChange={setCostLevel}
                                placeholder="Cost Level"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Value Mode */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Value Mode
                            </span>
                            <CustomSelectWithFilter
                                data={VALUE_MODE_OPTIONS}
                                value={valueMode}
                                onChange={setValueMode}
                                placeholder="Value Mode"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* View — Yearly (image 1) vs Quarterly (image 2) */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                View
                            </span>
                            <CustomSelectWithFilter
                                data={VIEW_MODE_OPTIONS}
                                value={viewMode}
                                onChange={setViewMode}
                                placeholder="View"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Projects Filter */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Projects Filter
                            </span>
                            <CustomMultiSelect
                                data={visibleProjectsForFilter.map((project) => ({
                                    label: project.PROJ_NAME,
                                    value: makeProjectKey(project.IO_NO, project.REV_NO),
                                }))}
                                value={selectedProjectKeys}
                                onChange={setSelectedProjectKeys}
                                placeholder="All Projects"
                                className="w-[280px]"
                            />
                        </div>

                        {/* Unit Filter */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Unit
                            </span>
                            <CustomSelectWithFilter
                                data={unitOptions}
                                value={unit}
                                onChange={setUnit}
                                placeholder="Unit"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Show Upto March — only meaningful in Crore mode */}
                        {valueMode !== "PERCENT" && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Inc. Upto March '{String(CURRENT_FY_START).slice(-2)}
                                </span>
                                <div className="flex items-center h-[42px] px-3 rounded-md border border-[#3b82f6] bg-[#eff6ff]">
                                    <Switch
                                        checked={showUptoMarch}
                                        onChange={setShowUptoMarch}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                {gettingProjectWiseCost ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                    </div>
                ) : (
                    <div>
                        <CustomTable
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            scroll={{ x: "max-content", y: 600 }}
                            rowClassName={(record) => (record.isTotalRow ? "dm-grand-total-row" : "")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DMReport;
